import type { AiChatPrompt } from '../aiChatPromptTypes';

export const investmentHoldingPrompts: AiChatPrompt[] = [
  {
    displayPrompt: '📈 How is this holding doing?',
    actualPrompt:
      "📈 How is this holding doing? Start with a one-sentence verdict (up, down, or flat overall). Then give me a short snapshot with: current market value, today's change in dollars and percent, and total gain or loss in dollars and percent. End with one plain-language takeaway about whether this holding looks healthy right now.",
  },
  {
    displayPrompt: '💵 What is my gain on this?',
    actualPrompt:
      '💵 What is my gain on this holding? Lead with my total gain or loss in dollars and percent. Then break down cost basis vs current market value so I understand the math. If cost basis is missing, incomplete, or differs by account, call that out clearly and say which numbers are reliable vs estimated. Keep it practical — I want to know if I am actually making or losing money on this position.',
  },
  {
    displayPrompt: '📊 How is it split across accounts?',
    actualPrompt:
      "📊 How is this holding split across my accounts? Show each account that holds it with: account name, quantity, market value, and percent of this holding's total value. Highlight which account holds the most and whether the split looks concentrated in one place. Present it as an easy-to-scan list, not a long paragraph.",
  },
  {
    displayPrompt: '🔍 What moved today?',
    useWebSearch: true,
    actualPrompt:
      "🔍 What moved today for this holding? Start with today's price change in percent and dollars, then how much that changed my position's value today. If there is recent news that may explain a large or unusual move, include it. If nothing conclusive appears in recent news, say so honestly.",
  },
  {
    displayPrompt: '📰 Latest news & insights',
    useWebSearch: true,
    actualPrompt:
      '📰 What is the latest news and what should I know as a holder? Give 2–4 bullet points on the most relevant recent themes and why they matter. Follow with a short "For your position" section tying news to my shares, market value, and recent performance. Do not invent headlines — if nothing recent is found, say so and summarize what my data shows.',
  },
  {
    displayPrompt: '💡 Summarize this holding',
    actualPrompt:
      "💡 Summarize this holding in under 30 seconds of reading. Include: symbol and name, total shares, market value, today's change, total gain or loss, and how it is split across accounts (one line if multiple). End with one sentence on the key thing I should take away. Be concise — bullets are fine.",
  },
  {
    displayPrompt: '🎯 What % of my portfolio is this?',
    actualPrompt:
      '🎯 What percent of my total investment portfolio does this holding represent? Show the market value of this position, my total portfolio value, and the percentage weight. Flag if this is a top-5 or outsized position relative to the rest of my portfolio. End with one sentence on whether this weight looks typical or unusually large.',
  },
  {
    displayPrompt: '💸 What dividend income does this pay?',
    actualPrompt:
      '💸 What dividend income does this holding generate? If dividend data is available, show estimated annual or trailing dividend income, yield if calculable, and any recent dividend payments. If this holding does not pay dividends or data is missing, say so clearly. Tie the answer to my share quantity so I know what income this position actually produces for me.',
  },
  {
    displayPrompt: '📉 How does this compare to its 52-week range?',
    useWebSearch: true,
    actualPrompt:
      '📉 How does this holding compare to its 52-week high and low? Show where the current price sits within that range (near high, mid-range, or near low) and what that means in plain language. Include the 52-week high and low prices when available from market data. Relate the range context to my cost basis and unrealized gain or loss.',
  },
  {
    displayPrompt: '✅ How reliable is my cost basis?',
    actualPrompt:
      '✅ How reliable is the cost basis for this holding? Review whether cost basis is present, complete, and consistent across accounts. Call out any accounts with missing, zero, or suspicious cost basis. Explain which gain or loss numbers I can trust and which may be estimates. Suggest what I should verify or fix if data quality is weak.',
  },
  {
    displayPrompt: '⚠️ Is this position too concentrated?',
    actualPrompt:
      "⚠️ Is this holding too concentrated in my portfolio? Compare this position's weight to my total portfolio and to typical diversification guidance. Note if it is a large single-stock bet or heavily weighted in one account. End with a practical take on concentration risk — not investment advice, but whether the size stands out relative to the rest of my holdings.",
  },
  {
    displayPrompt: '🏆 How is this doing vs my portfolio?',
    actualPrompt:
      "🏆 How is this holding performing compared to the rest of my portfolio? Compare its recent return and total gain or loss percent to my portfolio's overall performance over a similar period. Say clearly whether this holding is a top contributor, a drag, or in line with the pack. Keep it scannable with a short comparison table or bullets.",
  },
  {
    displayPrompt: '🧮 Show quantity by account',
    actualPrompt:
      '🧮 Show my share quantity for this holding in each account. List every account with: account name, share quantity, percent of total shares, and market value. Highlight any account where quantity changed recently if that data is available. I want a precise quantity breakdown, not just dollar values.',
  },
  {
    displayPrompt: '📋 Break down my unrealized gain',
    actualPrompt:
      '📋 Break down my unrealized gain or loss on this holding. Show total unrealized gain or loss in dollars and percent, then split by account if I hold it in multiple places. Explain cost basis vs market value for each slice. If any account has missing cost basis, separate realized-looking gains from numbers that may be incomplete.',
  },
  {
    displayPrompt: '⚖️ Should I add or trim this holding?',
    actualPrompt:
      '⚖️ Based on my data, should I consider adding to or trimming this holding? Review position size as a percent of portfolio, recent performance, gain or loss, and concentration. Frame the answer as factors to weigh — not a buy or sell recommendation. End with 2–3 concrete questions I should answer before making a decision.',
  },
  {
    displayPrompt: '🏭 How does this compare to its sector?',
    useWebSearch: true,
    actualPrompt:
      '🏭 How does this holding compare to its sector or peer group? Identify the sector or category when possible, then compare recent performance to sector benchmarks or representative peers. Tie the comparison back to my position size and gain or loss. If sector data is limited, say so and focus on what my holding data shows.',
  },
  {
    displayPrompt: '🧾 What should I know about tax lots?',
    actualPrompt:
      '🧾 What should I know about tax lots for this holding? Explain whether my data shows lot-level detail or only aggregate cost basis. Describe how selling from specific lots could affect gain or loss reporting in general terms. If lot detail is not available in my data, say so and note what I would need for tax-aware decisions.',
  },
  {
    displayPrompt: "📆 How has this holding's value trended?",
    actualPrompt:
      '📆 How has the market value of this holding trended over time? Describe the direction over recent weeks and months — growing, flat, or declining — using available history. Note any notable swings and how they relate to my total gain or loss. Keep it visual-friendly with a short timeline or bullet progression.',
  },
  {
    displayPrompt: '💪 Is my position size healthy?',
    actualPrompt:
      '💪 Is my position size healthy relative to my goals and portfolio? Review total shares, market value, and portfolio weight. Consider whether the size looks balanced, oversized, or undersized compared to my other holdings. Give a concise health check — not financial advice — with one clear takeaway about position sizing.',
  },
  {
    displayPrompt: '🔗 How does this compare to similar holdings?',
    actualPrompt:
      '🔗 How does this holding compare to similar holdings in my portfolio? Find other positions in the same asset class, sector, or style and compare market value, recent performance, and gain or loss. Highlight whether this one is outperforming or lagging its peers in my portfolio. Present as a short comparison list.',
  },
];
